const parseProductJsonResponse = async (response, fallbackMessage = 'Product could not be added to cart.') => {
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch (_error) {
    throw new Error(fallbackMessage);
  }
};

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('[type="submit"]');
        if (!this.form || !this.submitButton || !this.variantIdInput) return;

        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButtonText = this.submitButton.querySelector('.main-atc__label__text') || this.submitButton.querySelector('span');
        this.directCheckoutRequested = false;
        this.directCheckoutButton = null;
        this.activeActionButton = null;
        this.directCheckoutButtons = Array.from(this.querySelectorAll('[data-skip-cart-button="true"]'));
        this.actionButtons = [this.submitButton, ...this.directCheckoutButtons];
        this.acceleratedCheckoutUpsells = this.querySelector('[data-accelerated-checkout-upsells]');
        this.productInfo = this.closest('product-info');

        this.directCheckoutButtons.forEach((button) => {
          button.addEventListener('click', () => this.onDirectCheckout(button));
        });

        if (this.acceleratedCheckoutUpsells && this.productInfo && !this.closest('product-info-upsell')) {
          this.onUpsellChange = () => this.syncAcceleratedCheckoutUpsells();
          this.onUpsellInputChange = (event) => {
            if (
              event.target !== this.variantIdInput
              && !event.target.closest('product-info-upsell, quantity-break-upsells, quantity-gifts')
            ) return;
            queueMicrotask(() => this.syncAcceleratedCheckoutUpsells());
          };
          this.productInfo.addEventListener('upsell:change', this.onUpsellChange);
          this.productInfo.addEventListener('quantity-gifts:change', this.onUpsellChange);
          this.productInfo.addEventListener('quantity-break:change', this.onUpsellChange);
          this.productInfo.addEventListener('change', this.onUpsellInputChange);
          this.acceleratedCheckoutObserver = new MutationObserver((mutations) => {
            const addedUpsell = mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) =>
              node.nodeType === Node.ELEMENT_NODE
                && (
                  node.matches?.('product-info-upsell, quantity-break-upsells, quantity-gifts')
                  || node.querySelector?.('product-info-upsell, quantity-break-upsells, quantity-gifts')
                )
            ));
            if (addedUpsell) this.syncAcceleratedCheckoutUpsells();
          });
          this.acceleratedCheckoutObserver.observe(this.productInfo, { childList: true, subtree: true });
          this.syncAcceleratedCheckoutUpsells();
        }

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        const actionButton = this.directCheckoutRequested && this.directCheckoutButton
          ? this.directCheckoutButton
          : evt.submitter || this.submitButton;
        if (actionButton.getAttribute('aria-disabled') === 'true') return;
        if (!this.validateCustomProductFields()) {
          this.directCheckoutRequested = false;
          this.directCheckoutButton = null;
          return;
        }

        this.handleErrorMessage();

        this.activeActionButton = actionButton;
        this.setActionButtonsBusy(actionButton, true);

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        this.syncSelectedVariantInput();
        const formData = new FormData(this.form);
        this.removeAcceleratedCheckoutFields(formData);
        this.appendSelectedAdditionalItems(formData);
        const bundleVariantIds = formData.getAll('items[][id]').filter(Boolean);
        if (bundleVariantIds.length) {
          formData.delete('id');
          formData.delete('quantity');
        }
        const shouldSkipCart = this.directCheckoutRequested || this.variantIdInput?.dataset.skipCart === 'true';
        if (this.cart?.getSectionsToRender) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement?.(document.activeElement);
        }
        const cartRequest = this.buildMultiItemCartRequest(formData, bundleVariantIds);
        if (cartRequest) {
          config.headers['Content-Type'] = 'application/json';
          config.body = JSON.stringify(cartRequest);
        } else {
          config.body = formData;
        }

        const variantId = formData.get('id') || bundleVariantIds[0] || '';
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = bundleVariantIds.length ? null : this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then(async (response) => {
            const payload = await parseProductJsonResponse(response);
            if (!response.ok && !payload.status) payload.status = response.status || 'ERROR';
            return payload;
          })
          .then(async (response) => {
            if (response.status) {
              if (
                shouldSkipCart &&
                Number(response.status) === 422 &&
                await this.canCheckoutExistingCart(formData, bundleVariantIds)
              ) {
                this.resolveCartLinesUpdate(linesUpdateDeferred);
                this.submitGuestCheckout();
                return;
              }

              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (shouldSkipCart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              this.submitGuestCheckout();
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = window.CartPerformance?.createStartingMarker?.('add:wait-for-subscribers');
            if (!this.error) {
              try {
                Promise.resolve(
                  publish(PUB_SUB_EVENTS.cartUpdate, {
                    source: 'product-form',
                    productVariantId: variantId,
                    cartData: response,
                  })
                )
                  .then(() => {
                    window.CartPerformance?.measureFromMarker?.('add:wait-for-subscribers', startMarker);
                  })
                  .catch((eventError) => console.error(eventError));
              } catch (eventError) {
                console.error(eventError);
              }
            }
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            const completedActionButton = this.activeActionButton || this.submitButton;
            this.directCheckoutRequested = false;
            this.directCheckoutButton = null;
            this.activeActionButton = null;
            this.setActionButtonsBusy(completedActionButton, false);
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');

            window.CartPerformance?.measureFromEvent?.("add:user-action", evt);
          });
      }

      onDirectCheckout(button) {
        if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;
        this.directCheckoutRequested = true;
        this.directCheckoutButton = button;
        this.form.requestSubmit(this.submitButton);
      }

      submitGuestCheckout() {
        const cartUrl = window.routes?.cart_url || (typeof routes !== 'undefined' ? routes.cart_url : '/cart');
        const checkoutForm = document.createElement('form');
        checkoutForm.method = 'post';
        checkoutForm.action = cartUrl;
        checkoutForm.hidden = true;

        const checkoutInput = document.createElement('input');
        checkoutInput.type = 'hidden';
        checkoutInput.name = 'checkout';
        checkoutInput.value = 'Checkout';
        checkoutForm.appendChild(checkoutInput);
        document.body.appendChild(checkoutForm);
        checkoutForm.submit();
      }

      syncSelectedVariantInput() {
        const submittedVariantId = this.form.dataset.submitVariantId;
        if (submittedVariantId && this.variantIdInput) {
          this.variantIdInput.value = submittedVariantId;
          delete this.form.dataset.submitVariantId;
          return;
        }

        const bundlePrimaryId = this.getSelectedBundleVariantIds()[0];
        if (bundlePrimaryId && this.variantIdInput) {
          this.variantIdInput.value = bundlePrimaryId;
          return;
        }

        const productInfo = this.closest('product-info');
        const variantPicker = productInfo?.querySelector('variant-selects');
        const selectedVariant = variantPicker?.getSelectedVariant?.();
        if (!selectedVariant?.id || !this.variantIdInput) return;

        this.variantIdInput.value = String(selectedVariant.id);
      }

      syncAcceleratedCheckoutUpsells() {
        if (!this.acceleratedCheckoutUpsells || !this.productInfo) return;

        this.syncSelectedVariantInput();
        this.acceleratedCheckoutUpsells.replaceChildren();
        const bundleVariantIds = this.getSelectedBundleVariantIds();
        const mainVariantId = String(bundleVariantIds[0] || this.variantIdInput?.value || '');
        if (mainVariantId && this.variantIdInput) this.variantIdInput.value = mainVariantId;
        const selectedUpsells = this.getSelectedAdditionalItems();
        let itemIndex = 1;

        bundleVariantIds.slice(1).forEach((variantId) => {
          this.appendAcceleratedCheckoutInput(itemIndex, 'id', variantId);
          this.appendAcceleratedCheckoutInput(itemIndex, 'quantity', '1');
          itemIndex += 1;
        });

        selectedUpsells.forEach((upsell) => {
          const variantId = String(upsell.id || '');
          if (!variantId) return;

          this.appendAcceleratedCheckoutInput(itemIndex, 'id', variantId);
          this.appendAcceleratedCheckoutInput(itemIndex, 'quantity', upsell.quantity || '1');
          if (mainVariantId) this.appendAcceleratedCheckoutInput(itemIndex, 'parent_id', mainVariantId);
          if (upsell.sellingPlan) this.appendAcceleratedCheckoutInput(itemIndex, 'selling_plan', upsell.sellingPlan);
          Object.entries(upsell.properties || {}).forEach(([name, value]) => {
            if (value !== '') this.appendAcceleratedCheckoutInput(itemIndex, `properties][${name}`, value);
          });
          itemIndex += 1;
        });
      }

      getSelectedBundleVariantIds() {
        if (!this.form) return [];
        return Array.from(this.form.querySelectorAll('.product-form__variants input[name="items[][id]"]'))
          .map((input) => String(input.value || ''))
          .filter(Boolean);
      }

      appendAcceleratedCheckoutInput(itemIndex, attribute, value) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `shopify-additional-items[${itemIndex}][${attribute}]`;
        input.value = String(value);
        this.acceleratedCheckoutUpsells.appendChild(input);
      }

      removeAcceleratedCheckoutFields(formData) {
        Array.from(formData.keys()).forEach((name) => {
          if (name.startsWith('shopify-additional-items[')) formData.delete(name);
        });
      }

      buildMultiItemCartRequest(formData, bundleVariantIds) {
        if (!bundleVariantIds.length) return null;

        const items = [];
        let currentItem = null;
        const mainProperties = {};

        formData.forEach((value, name) => {
          if (name === 'items[][id]') {
            currentItem = { id: Number(value), quantity: 1 };
            items.push(currentItem);
            return;
          }
          if (name === 'items[][quantity]' && currentItem) {
            currentItem.quantity = Math.max(1, parseInt(value, 10) || 1);
            return;
          }
          if (name === 'items[][selling_plan]' && currentItem && value) {
            currentItem.selling_plan = Number(value);
            return;
          }
          if (name === 'items[][parent_id]' && currentItem && value) {
            currentItem.parent_id = Number(value);
            return;
          }
          const itemPropertyMatch = name.match(/^items\[\]\[properties\]\[(.+)\]$/);
          if (itemPropertyMatch && currentItem && value !== '') {
            currentItem.properties ||= {};
            currentItem.properties[itemPropertyMatch[1]] = String(value);
            return;
          }

          const propertyMatch = name.match(/^properties\[(.+)\]$/);
          if (propertyMatch && value !== '') mainProperties[propertyMatch[1]] = String(value);
        });

        if (!items.length) return null;
        const mainSellingPlan = formData.get('selling_plan');
        items.filter((item) => !item.parent_id).forEach((item) => {
          if (Object.keys(mainProperties).length) item.properties = { ...mainProperties, ...(item.properties || {}) };
          if (mainSellingPlan && !item.selling_plan) item.selling_plan = Number(mainSellingPlan);
        });

        const request = { items };
        const sections = formData.get('sections');
        const sectionsUrl = formData.get('sections_url');
        if (sections) request.sections = String(sections);
        if (sectionsUrl) request.sections_url = String(sectionsUrl);
        return request;
      }

      getSelectedAdditionalItems() {
        if (!this.productInfo) return [];
        const items = [];
        this.productInfo.querySelectorAll('product-info-upsell[data-selected="true"][data-id]').forEach((upsell) => {
          if (upsell.dataset.variantAvailable === 'false') return;
          items.push({
            id: upsell.dataset.id,
            quantity: '1',
            key: upsell.dataset.upsellKey || `${upsell.dataset.productId || ''}-${upsell.dataset.id}`,
            properties: {
              _theme_upsell_key: upsell.dataset.upsellKey || `${upsell.dataset.productId || ''}-${upsell.dataset.id}`,
            },
            sellingPlan: upsell.querySelector('input[name="selling_plan"]:not(:disabled)')?.value || '',
          });
        });
        this.productInfo
          .querySelectorAll('quantity-break-upsells:not([hidden]) [data-additional-key][data-product][data-selected="true"]')
          .forEach((item) => {
            items.push({
              id: item.dataset.product,
              quantity: '1',
              key: item.dataset.additionalKey,
              properties: {
                _theme_additional_key: item.dataset.additionalKey,
                _theme_parent_product_id: this.productInfo.dataset.productId || '',
                _theme_min_parent_quantity: item.dataset.minQuantity || '1',
                _theme_additional_kind: item.dataset.type || 'upsell',
              },
              sellingPlan: item.querySelector('input[name="selling_plan"]:not(:disabled)')?.value || '',
            });
          });
        this.productInfo
          .querySelectorAll('quantity-gifts .quantity-gift[data-additional-key][data-product][data-unlocked="true"]')
          .forEach((gift) => {
            items.push({
              id: gift.dataset.product,
              quantity: '1',
              key: gift.dataset.additionalKey,
              properties: {
                _theme_additional_key: gift.dataset.additionalKey,
                _theme_parent_product_id: this.productInfo.dataset.productId || '',
                _theme_min_parent_quantity: gift.dataset.quantity || '1',
                _theme_additional_kind: 'gift',
              },
              sellingPlan: gift.querySelector('input[name="selling_plan"]:not(:disabled)')?.value || '',
            });
          });
        const seen = new Set();
        return items.filter((item) => {
          const identity = `${Object.keys(item.properties || {})[0] || ''}:${item.key}`;
          if (!item.id || !item.key || seen.has(identity)) return false;
          seen.add(identity);
          return true;
        });
      }

      setActionButtonsBusy(activeButton, busy) {
        this.actionButtons.forEach((button) => {
          if (busy) {
            if (button.dataset.productFormBusy !== 'true') {
              button.dataset.productFormBusy = 'true';
              button.dataset.productFormPreviousAria = button.hasAttribute('aria-disabled')
                ? button.getAttribute('aria-disabled')
                : '__missing__';
            }
            button.setAttribute('aria-disabled', 'true');
          } else if (button.dataset.productFormBusy === 'true') {
            const previousAria = button.dataset.productFormPreviousAria;
            if (previousAria === '__missing__') button.removeAttribute('aria-disabled');
            else button.setAttribute('aria-disabled', previousAria || 'false');
            delete button.dataset.productFormBusy;
            delete button.dataset.productFormPreviousAria;
          }
        });

        activeButton?.classList.toggle('loading', busy);
        activeButton
          ?.querySelector('.loading-overlay__spinner, .loading__spinner')
          ?.classList.toggle('hidden', !busy);
      }

      async canCheckoutExistingCart(formData, bundleVariantIds) {
        const fields = Array.from(formData.entries());
        const hasSellingPlan = fields.some(([name, value]) => name.includes('selling_plan') && value);
        const hasLineProperties = fields.some(([name, value]) => name.includes('properties[') && value);
        if (hasSellingPlan || hasLineProperties) return false;

        const variantIds = bundleVariantIds.length
          ? formData.getAll('items[][id]').filter(Boolean)
          : [formData.get('id')].filter(Boolean);
        const quantities = bundleVariantIds.length
          ? formData.getAll('items[][quantity]')
          : [formData.get('quantity')];
        if (!variantIds.length) return false;

        const requestedQuantities = new Map();
        variantIds.forEach((variantId, index) => {
          const id = String(variantId);
          const quantity = Math.max(1, parseInt(quantities[index], 10) || 1);
          requestedQuantities.set(id, (requestedQuantities.get(id) || 0) + quantity);
        });

        try {
          const cartPath = window.routes?.cart_url || (typeof routes !== 'undefined' ? routes.cart_url : '/cart');
          const response = await fetch(`${cartPath.replace(/\/$/, '')}.js`, {
            headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          });
          if (!response.ok) return false;

          const cart = await response.json();
          const cartQuantities = new Map();
          (cart.items || []).forEach((item) => {
            const id = String(item.variant_id || item.id || '');
            if (!id) return;
            cartQuantities.set(id, (cartQuantities.get(id) || 0) + (parseInt(item.quantity, 10) || 0));
          });

          return Array.from(requestedQuantities.entries()).every(
            ([variantId, quantity]) => (cartQuantities.get(variantId) || 0) >= quantity
          );
        } catch (_error) {
          return false;
        }
      }

      validateCustomProductFields() {
        if (!this.productInfo) return true;
        const fields = Array.from(this.productInfo.querySelectorAll('custom-product-field'));
        const firstInvalid = fields.find((field) => field.validate?.(false) === false);
        if (!firstInvalid) return true;
        firstInvalid.validate?.(true);
        return false;
      }

      appendSelectedAdditionalItems(formData) {
        if (this.closest('product-info-upsell')) return;

        const selectedUpsells = this.getSelectedAdditionalItems();
        if (!selectedUpsells.length) return;

        const existingVariantIds = formData.getAll('items[][id]').filter(Boolean).map(String);
        let mainVariantId = existingVariantIds[0] || String(formData.get('id') || '');
        if (!existingVariantIds.length) {
          mainVariantId = String(formData.get('id') || '');
          if (!mainVariantId) return;

          const mainQuantity = formData.get('quantity') || '1';
          const mainSellingPlan = formData.get('selling_plan');
          formData.delete('id');
          formData.delete('quantity');
          formData.delete('selling_plan');
          formData.append('items[][id]', mainVariantId);
          formData.append('items[][quantity]', mainQuantity);
          if (mainSellingPlan) formData.append('items[][selling_plan]', mainSellingPlan);
        }

        selectedUpsells.forEach((upsell) => {
          formData.append('items[][id]', String(upsell.id));
          formData.append('items[][quantity]', String(upsell.quantity || '1'));
          if (mainVariantId) formData.append('items[][parent_id]', mainVariantId);
          if (upsell.sellingPlan) formData.append('items[][selling_plan]', upsell.sellingPlan);
          Object.entries(upsell.properties || {}).forEach(([name, value]) => {
            if (value !== '') formData.append(`items[][properties][${name}]`, value);
          });
        });
      }

      disconnectedCallback() {
        this.acceleratedCheckoutObserver?.disconnect();
        this.productInfo?.removeEventListener('upsell:change', this.onUpsellChange);
        this.productInfo?.removeEventListener('quantity-gifts:change', this.onUpsellChange);
        this.productInfo?.removeEventListener('quantity-break:change', this.onUpsellChange);
        this.productInfo?.removeEventListener('change', this.onUpsellInputChange);
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText) this.submitButtonText.textContent = this.submitButton.dataset.customLabel || window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      measure(name, callback) {
        if (window.CartPerformance?.measure) window.CartPerformance.measure(name, callback);
        else callback();
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

    }
  );
}
